import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TreeViewList from './tree-view-list';
import TreeListItemDragPreview from './tree-view-item-drag-preview';
import { TreeViewActionType } from './constants/types';
import noop from '../utils/noop';
import getParentOrder from './helpers/get-parent-order';
import getOriginalItem from './helpers/get-original-item';
import dropPositions from './constants/drop-positions';
import { NONE, SORTABLE, NESTABLE } from './constants/interaction-types';

class TreeView extends React.Component {
  static comparator(a, b) {
    const arrayOfA = a.order.split('.');
    const arrayOfB = b.order.split('.');

    const aLength = arrayOfA.length;
    const bLength = arrayOfB.length;

    const minArrayLength = Math.min(aLength, bLength);

    for (let index = 0; index < minArrayLength; index += 1) {
      if (Number(arrayOfA[index]) > Number(arrayOfB[index])) {
        return 1;
      }

      if (Number(arrayOfA[index]) < Number(arrayOfB[index])) {
        return -1;
      }
    }

    if (aLength !== bLength) {
      return aLength > bLength ? 1 : -1;
    }

    return 0;
  }

  static changeItemPosition(items, sourceItem, dropConfig, initialValue = []) {
    return items.reduce((result, item) => {
      if (item.id === sourceItem.id) {
        return result;
      }

      if (dropConfig.target.id === item.id) {
        switch (dropConfig.position) {
          case dropPositions.BEFORE:
            result.push(sourceItem, {
              ...item,
              items: TreeView.changeItemPosition(item.items, sourceItem, dropConfig),
            });
            break;

          case dropPositions.AFTER:
            result.push(
              {
                ...item,
                items: TreeView.changeItemPosition(item.items, sourceItem, dropConfig),
              },
              sourceItem
            );
            break;

          case dropPositions.INSIDE:
            result.push({
              ...item,
              items: TreeView.changeItemPosition(item.items, sourceItem, dropConfig, [sourceItem]),
            });
            break;

          default:
            result.push({
              ...item,
              items: TreeView.changeItemPosition(item.items, sourceItem, dropConfig),
            });
        }
      } else {
        result.push({
          ...item,
          items: TreeView.changeItemPosition(item.items, sourceItem, dropConfig),
        });
      }

      return result;
    }, initialValue);
  }

  static prepareItems(items) {
    const map = {};

    return items.sort(this.comparator).reduce((result, item) => {
      const extendedItem = {
        ...item,
        items: [],
      };

      if (!map[item.order]) {
        map[item.order] = extendedItem;
      }

      const targetVersion = getParentOrder(item.order);

      if (targetVersion) {
        map[targetVersion].items.push(extendedItem);
      } else {
        result.push(extendedItem);
      }

      return result;
    }, []);
  }

  static prepareResult(listOfItems, nestingLevel = '') {
    const prefix = nestingLevel ? `${nestingLevel}.` : '';

    return listOfItems.reduce((result, item, index) => {
      const originalItem = getOriginalItem(item);

      const order = `${prefix}${index + 1}`;
      result.push(
        {
          ...originalItem,
          order,
        },

        ...TreeView.prepareResult(item.items, order)
      );

      return result;
    }, []);
  }

  constructor(props) {
    super(props);

    this.containerRef = React.createRef();

    this.state = {
      items: [],
    };

    this.move = this.move.bind(this);
    this.find = this.find.bind(this);
  }

  componentDidMount() {
    const { items } = this.props;

    this.checkNestingLevel();

    this.setState({
      items: TreeView.prepareItems(items),
    });
  }

  componentDidUpdate(prevProps) {
    const { items } = this.props;

    if (items !== prevProps.items) {
      this.checkNestingLevel();
      // eslint-disable-next-line yola/react/no-did-update-set-state
      this.setState({
        items: TreeView.prepareItems(items),
      });
    }
  }

  find(id, items) {
    // eslint-disable-next-line no-restricted-syntax
    for (const viewItem of items) {
      if (viewItem.id === id) {
        return viewItem;
      }

      if (viewItem.items.length > 0) {
        const targetItem = this.find(id, viewItem.items);

        if (targetItem) {
          return targetItem;
        }
      }
    }

    return null;
  }

  move(dragSource, dropTarget, position) {
    if (dragSource.item.id === dropTarget.item.id) {
      return;
    }

    const { items } = this.state;
    const { onOrderChange } = this.props;
    const newItems = TreeView.changeItemPosition(items, dragSource.item, {
      target: dropTarget.item,
      position,
    });

    this.setState(
      {
        items: newItems,
      },
      () => onOrderChange(TreeView.prepareResult(newItems))
    );
  }

  checkNestingLevel() {
    const { maxNestingLevel, items } = this.props;

    const isBiggerThanMax = items.some((item) => item.order.split('.').length > maxNestingLevel);

    if (isBiggerThanMax) {
      throw new Error(
        `Max nesting level is ${maxNestingLevel}, please check 'order' in your items,
          your order should be no more than '${'.*'.repeat(maxNestingLevel).replace(/^\./, '')}'`
      );
    }
  }

  render() {
    const {
      maxNestingLevel,
      interaction,
      onItemClick,
      onStartItemDrag,
      onEndItemDrag,
      portalContainer,
      translate,
    } = this.props;
    const { items } = this.state;
    const className = classNames('ws-tree-view', {
      'ws-tree-view--interaction-disabled': interaction === NONE,
    });

    return (
      <div className={className} ref={this.containerRef} {...(translate && { translate })}>
        <TreeViewList
          items={items}
          move={this.move}
          interaction={interaction}
          find={this.find}
          maxNestingLevel={maxNestingLevel}
          nestingLevel={1}
          onItemClick={onItemClick}
          onStartItemDrag={onStartItemDrag}
          onEndItemDrag={onEndItemDrag}
        />
        {portalContainer ? (
          ReactDOM.createPortal(
            <TreeListItemDragPreview containerEl={portalContainer} />,
            portalContainer
          )
        ) : (
          <TreeListItemDragPreview containerEl={this.containerRef.current} />
        )}
      </div>
    );
  }
}

TreeView.propTypes = {
  onOrderChange: PropTypes.func,
  onItemClick: PropTypes.func,
  maxNestingLevel: PropTypes.number,
  interaction: PropTypes.oneOf([NONE, SORTABLE, NESTABLE]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      order: PropTypes.string.isRequired,
      actions: PropTypes.arrayOf(TreeViewActionType),
    })
  ),
  onStartItemDrag: PropTypes.func,
  onEndItemDrag: PropTypes.func,
  portalContainer: PropTypes.shape(),
  translate: PropTypes.oneOf(['no', 'yes']),
};

TreeView.defaultProps = {
  interaction: NESTABLE,
  onOrderChange: noop,
  onItemClick: noop,
  maxNestingLevel: 3,
  items: [],
  onStartItemDrag: noop,
  onEndItemDrag: noop,
  portalContainer: null,
  translate: null,
};

export default TreeView;
